import React, { useContext, useRef } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { UserContext, LoadDetailStatusContext } from 'components/Providers/Providers';
import { MediaQueryContext } from '@arrive/responsive-provider';
import { useMatch, useLocation } from 'react-router-dom';
import cx from 'classnames'

import { ROUTES } from 'constants/routeConstants';

import ARRIVENOW_LOGO from 'assets/arrive-now-logo.svg'
import ARRIVENOW_LOGO_DARK from 'assets/arrive-now-logo-dark.svg'
import ACCOUNT from '@arrive/ui-icons/duo-tone/ic_account_circle.svg';
import COMPANY from '@arrive/ui-icons/duo-tone/ic_my_company_duo.svg';
import CONTACT from '@arrive/ui-icons/duo-tone/ic_support_duo.svg';

import NavBar from '@arrive/nav-bar';

import styles from './navigation.scss';
import { showDedicatedOpportunities } from 'utils/permissionUtils';

export default function NavigationBar() {
  const { oktaAuth } = useOktaAuth()
  const { FEATURE_LANE_OPPORTUNITIES, BYPASS_UNIT_COUNT_CHECK } = useFlags()

  const currentUser = useContext(UserContext);
  const currentMediaTypes = useContext(MediaQueryContext);
  const { pathname } = useLocation();
  const useDark = pathname === ROUTES.landing;
  const loadDetailLoadIsAvailable = useContext(LoadDetailStatusContext);
  const isLoadDetail = !!useMatch(ROUTES.loadDetail);
  const isTest = useRef(window.location.host === 'carrier-test.arrivelogistics.com');
  const isStaging = useRef(window.location.host === 'carrier-stg.arrivelogistics.com');
  const isDev = useRef(window.location.host === 'carrier-dev.arrivelogistics.com');
  const isMyLoads = !!useMatch(ROUTES.myLoads) || (isLoadDetail && !loadDetailLoadIsAvailable)
  const isMyOffers = !!useMatch(ROUTES.myOffers);
  const isCPCommittedCapacity = !!useMatch(ROUTES.committedCapacity)
  const isLaneOpportunities = !!useMatch(ROUTES.dedicatedOpportunities)
  const logo = useDark ? ARRIVENOW_LOGO : ARRIVENOW_LOGO_DARK

  const mainLinkToItems = [
    {
      to: ROUTES.loadBoard,
      title: 'Find Loads',
      isSelected: !!useMatch(ROUTES.loadBoard) || (isLoadDetail && loadDetailLoadIsAvailable)
    }
  ];
  const userLinkToItems = [
    {
      to: ROUTES.profile,
      title: 'My Profile',
      svg: ACCOUNT
    }
  ];

  if (currentUser.CarrierId) {
    mainLinkToItems.push({
      to: ROUTES.myLoads,
      title: 'My Loads',
      isSelected: isMyLoads
    })

    mainLinkToItems.push({
      to: ROUTES.myOffers,
      title: 'My Offers',
      isSelected: isMyOffers
    })

    userLinkToItems.push({
      to: ROUTES.myCompany,
      title: 'My Company',
      svg: COMPANY
    })
    if(currentUser.Committed){
      mainLinkToItems.push({
        to: ROUTES.committedCapacity,
        title: 'Committed Capacity',
        isSelected: isCPCommittedCapacity
      })
    }

    if(showDedicatedOpportunities(currentUser, false, FEATURE_LANE_OPPORTUNITIES, BYPASS_UNIT_COUNT_CHECK)){
      mainLinkToItems.push({
        to: ROUTES.dedicatedOpportunities,
        title: 'Dedicated Opportunities',
        isSelected: isLaneOpportunities
      })
    }
  }


  userLinkToItems.push({
    to: ROUTES.contactUs,
    title: 'Contact Us',
    svg: CONTACT
  })

  const onLogout = () => {
    oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
  }

  const devEnvWarning = <>{(isStaging.current || isTest.current || isDev.current) && <div className={styles.devMode}>ENV: {isStaging.current && 'STAGE'}{isTest.current && 'TEST'}{isDev.current && 'DEV'}</div>}</>

  if (isLoadDetail && (currentMediaTypes.xs || currentMediaTypes.sm)) {
    return devEnvWarning;
  }

  return (
    <>
      <NavBar
        className={cx(styles.navBar, { [styles.roadhog]: useDark })}
        logoClassName={styles.logo}
        currentUser={currentUser}
        currentMediaTypes={currentMediaTypes}
        homeLinkTo={ROUTES.landing}
        userLinkToItems={userLinkToItems}
        mainLinkToItems={mainLinkToItems}
        theme={useDark ? 'roadhog' : 'ethanol'}
        onLogout={onLogout}
        logo={logo}
      />
      {devEnvWarning}
    </>
  )
}
